import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { createAuthGuard } from '@auth0/auth0-vue';
import { globalState } from '@/stores/globalState';

// Eager: only the public landing page, which is the first paint at '/'.
import Hero from '@/components/Hero.vue';

// Lazy views — code-split so the eager bundle stays small. Overview (the
// chart-heavy dashboard) was previously eager and shipped to every visitor,
// including the public landing page; it's behind auth, so it's lazy now.
const Overview = () => import('@/components/Overview.vue');
const Onboarding = () => import('@/components/Onboarding.vue');
const Profile = () => import('@/components/Profile.vue');
const HouseholdSettings = () => import('@/components/HouseholdSettings.vue');
const ConnectedAccounts = () => import('@/views/ConnectedAccounts.vue');
const ViewExpenses = () => import('@/components/ViewExpenses.vue');
const RulesManager = () => import('@/components/RulesManager.vue');
const ChatView = () => import('@/views/ChatView.vue');
const PrivacyPolicy = () => import('@/views/PrivacyPolicy.vue');
const TermsOfService = () => import('@/views/TermsOfService.vue');

const NotFound = { template: '<div class="p-6 text-center">Page not found.</div>' };

const routes: RouteRecordRaw[] = [
  { path: '/', name: 'Home', component: Hero },

  { path: '/auth/callback', name: 'Auth0Callback',
    component: () => import('@/components/AuthCallback.vue') },

  { path: '/overview',  name: 'Overview',  component: Overview,          meta: { requiresAuth: true } },
  { path: '/expenses',  name: 'Expenses',  component: ViewExpenses,      meta: { requiresAuth: true } },
  { path: '/rules',     name: 'Rules',     component: RulesManager,      meta: { requiresAuth: true } },
  { path: '/accounts',  name: 'Accounts',  component: ConnectedAccounts, meta: { requiresAuth: true } },
  { path: '/chat',      name: 'Chat',      component: ChatView,          meta: { requiresAuth: true } },
  { path: '/settings',  name: 'Settings',  component: Profile,           meta: { requiresAuth: true } },
  { path: '/household', name: 'HouseholdSettings', component: HouseholdSettings, meta: { requiresAuth: true } },
  { path: '/onboarding', name: 'Onboarding', component: Onboarding,      meta: { requiresAuth: true } },

  // Legacy paths — redirect to the new canonical routes.
  { path: '/view-expenses',      redirect: { name: 'Expenses' } },
  { path: '/input-expenses',     redirect: { name: 'Expenses', query: { add: '1' } } },
  { path: '/connected-accounts', redirect: { name: 'Accounts' } },
  { path: '/plaid',              redirect: { name: 'Accounts' } },
  { path: '/profile',            redirect: { name: 'Settings' } },

  // Public legal pages (no auth required)
  { path: '/privacy', name: 'PrivacyPolicy', component: PrivacyPolicy },
  { path: '/terms',   name: 'TermsOfService', component: TermsOfService },

  // 404 catch-all
  { path: '/:pathMatch(.*)*', name: 'NotFound', component: NotFound },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() { return { top: 0 } },
});

const guard = createAuthGuard({
  redirectLoginOptions: {
    appState: (to: any) => ({ target: to.fullPath }),
  },
});

router.beforeEach((to) => {
  if (to.meta?.requiresAuth) {
    const authResult = guard(to);
    if (globalState.onboardingCompleted !== null) {
      if (!globalState.onboardingCompleted && to.name !== 'Onboarding') {
        return { name: 'Onboarding' };
      }
      // Already-onboarded users can still replay the flow from Settings
      // via ?preview=1 (read by Onboarding.vue to skip the completion POST).
      if (
        globalState.onboardingCompleted &&
        to.name === 'Onboarding' &&
        to.query.preview !== '1'
      ) {
        return { name: 'Overview' };
      }
    }
    return authResult;
  }
});

export default router;
